import { Box, Chip, CircularProgress, Paper, Stack, TableBody, TableCell, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { FC, memo, useMemo } from 'react';
import { BERTH_RESERVATION_STATUS_CONFIG } from 'src/constants/berthReservation';
import { LaneLocationSummary, ShipmentOrderLane } from 'src/entities/ShipmentOrderLane';

type Props = {
  lanes: ShipmentOrderLane[];
  isLoading: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
  };
  pageSize: number;
  onRowClick: (lane: ShipmentOrderLane) => void;
  onPageChange: (page: number) => void;
};

/**
 * レーンテーブルコンポーネント
 */
const LaneTable: FC<Props> = memo(({
  lanes,
  isLoading,
  pagination,
  pageSize,
  onRowClick,
  onPageChange,
}) => {
  const statusConfig = useMemo(() => ({
    not_planned: { label: '未配車', color: 'error' as const },
    partial: { label: '一部配車', color: 'primary' as const },
    completed: { label: '配車済', color: 'success' as const },
  }), []);

  const formatDate = (value?: string | null) => {
    if (!value) return '--';
    try {
      return format(new Date(value), 'MM/dd (EEE)', { locale: ja });
    } catch {
      return '--';
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '--';
    try {
      return format(new Date(value), 'yyyy/MM/dd HH:mm', { locale: ja });
    } catch {
      return '--';
    }
  };

  const formatVolume = (summary: ShipmentOrderLane['volumeSummary']) => {
    if (!summary) return '--';
    const parts = [];
    if (summary.packages) parts.push(`${summary.packages.toLocaleString()}個`);
    if (summary.weightKg) parts.push(`${summary.weightKg.toLocaleString()}kg`);
    if (summary.volumeM3) parts.push(`${summary.volumeM3.toLocaleString()}㎥`);
    return parts.length ? parts.join(' / ') : '--';
  };

  const renderAssignments = (lane: ShipmentOrderLane) => {
    if (!lane) return;

    const assignedStatus = lane.laneStatus || '-';
    const truckCount = lane.assignedTruckCount || 0;
    const autoAssignment = lane.autoAssignments?.[0];
    const manualAssignment = lane.manualAssignments?.[0];

    // 表示用に統一した形に変換
    const displayAssignment = autoAssignment
      ? {
          carrierCompany: autoAssignment.carrierCompany,
          operatingCarrier: autoAssignment.operatingCarrier,
          driverName: autoAssignment.driverName,
          truckNumber: autoAssignment.truckNumber,
          hierarchyLevel: autoAssignment.hierarchyLevel,
        }
      : manualAssignment
        ? {
            carrierCompany: manualAssignment.carrier_company,
            operatingCarrier: manualAssignment.operating_carrier,
            driverName: manualAssignment.driver_name,
            truckNumber: manualAssignment.truck_number,
            hierarchyLevel: manualAssignment.hierarchy_level,
          }
        : null;

    return (
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Box>
          {assignedStatus !== '-' && (
            <Chip
              size="small"
              label={statusConfig[assignedStatus as keyof typeof statusConfig].label}
              color={statusConfig[assignedStatus as keyof typeof statusConfig].color}
            />
          )}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            配車済: {truckCount}台
          </Typography>
        </Box>
        {truckCount > 1 && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              詳細画面で確認
            </Typography>
          </Box>
        )}
        {truckCount === 1 && displayAssignment && (
          <Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '90%'}}>
                運送会社: {displayAssignment.carrierCompany || '--'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '90%'}}>
                実走行会社: {displayAssignment.operatingCarrier || '--'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '90%'}}>
                ドライバー: {displayAssignment.driverName || '--'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '90%'}}>
                車番: {displayAssignment.truckNumber || '--'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '90%'}}>
                請負階層: {Number(displayAssignment.hierarchyLevel) > 0 ? `${displayAssignment.hierarchyLevel}次受け` : '--'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderLocationBlock = (location?: LaneLocationSummary) => {
    const berthReservationStatus = BERTH_RESERVATION_STATUS_CONFIG[(location?.berthReservationStatus || 'not_required') as keyof typeof BERTH_RESERVATION_STATUS_CONFIG];
    if (!location) {
      return (
        <Typography variant="body2" color="text.secondary">
          情報がありません
        </Typography>
      );
    }
    return (
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Tooltip title={location.address || '--'}>
          <Typography variant="caption" color="text.primary" noWrap>
            {location.address || '--'}
          </Typography>
        </Tooltip>
        <Typography variant="body2" color="text.primary">
          {formatDate(location.scheduledDate)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '80%' }}>
          {location.timeRange || '時間指定なし'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '80%' }}>
          {location.stayingMinutes ? `滞在: ${location.stayingMinutes} 分` : ''}
        </Typography>
        <Stack direction="row" alignItems="baseline" sx={{ pt: 1}}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1}}>
            バース予約:
          </Typography>
          <Chip
            size="small"
            label={berthReservationStatus.label}
            color={berthReservationStatus.color}
            sx={{ p: 0, m: 0 }}
          />
        </Stack>
      </Box>
    );
  };

  return (
    <Paper sx={{ height: 'calc(100vh - 250px)', minHeight: 400, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, overflow: 'auto', position: 'relative', border: '1px solid', borderColor: 'divider' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            minWidth: 1400,
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& thead': {
              position: 'sticky',
              top: 0,
              zIndex: 1,
            },
            '& thead th': {
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.secondary',
              textAlign: 'left',
              py: 1.5,
              px: 2,
              whiteSpace: 'nowrap',
            },
            '& tbody td': {
              borderBottom: '1px solid',
              borderColor: 'divider',
              verticalAlign: 'top',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>レーン情報</TableCell>
              <TableCell>区間・物量</TableCell>
              <TableCell>積地情報</TableCell>
              <TableCell>降地情報</TableCell>
              <TableCell>配車情報</TableCell>
              <TableCell>配車担当者</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lanes.map((lane) => {
              return (
                <TableRow
                  key={lane.id}
                  hover
                  onClick={() => onRowClick(lane)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        レーンID: {lane.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        伝票数: {lane.shipmentOrderCount.toLocaleString()}
                      </Typography>
                      <Chip
                        size="small"
                        label={lane.stats}
                        variant="outlined"
                        sx={{ width: 'fit-content' }}
                      />
                      {lane.deliveryMethods && (
                        <Chip
                          size="small"
                          label={lane.deliveryMethods}
                          variant="outlined"
                          sx={{ width: 'fit-content' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography variant="subtitle2" color="primary.main">
                        {lane.loadingLocation?.name || '--'} → {lane.unloadingLocation?.name || '--'}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatVolume(lane.volumeSummary)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        支払総額: {lane.totalPaymentAmountYen ? `${lane.totalPaymentAmountYen.toLocaleString()} 円` : '--'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {renderLocationBlock(lane.loadingLocation)}
                  </TableCell>
                  <TableCell>
                    {renderLocationBlock(lane.unloadingLocation)}
                  </TableCell>
                  <TableCell>
                    {renderAssignments(lane)}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        編集者: {lane.latestAssignmentEditors.length > 0 ? lane.latestAssignmentEditors.join(', ') : '--'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        最新更新: {lane.latestAssignmentAt ? formatDateTime(lane.latestAssignmentAt) : '--'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Box>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {!isLoading && lanes.length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            データがありません
          </Box>
        )}
      </Box>
      <TablePagination
        component="div"
        count={pagination.totalCount}
        page={Math.max(0, pagination.currentPage - 1)}
        onPageChange={(_, nextPage) => onPageChange(nextPage + 1)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={() => {
          // 固定件数のため変更処理は不要
        }}
        rowsPerPageOptions={[pageSize]}
        labelRowsPerPage="表示件数"
      />
    </Paper>
  );
});

export default LaneTable;
